import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import {DataType, DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import type {FilterOption} from "crqlar-react-common/dist/models/audience/filterOption.model";
import type {GuestAppListRequestDTO} from "crqlar-react-common/dist/models/guestApp/guestAppListRequest.dto";
import type {GuestAppUser} from "crqlar-react-common/dist/models/guestApp/guestAppUser.model";
import {GuestAppUserService} from "crqlar-react-common/dist/services/guestApp/guestAppUser.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import {DateElement} from "../../common/uiElements/DateElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import SettingsListPage from "../layout/SettingsListPage";

const textFilterValues = (rules: FilterRule[], field: string) => {
    const values = rules
        .filter(rule => rule.field === field)
        .flatMap(rule => rule.textValues ?? [])
        .filter((value): value is string => !!value);

    return values.length ? Array.from(new Set(values)) : undefined;
};

const dateRangeFilter = (rules: FilterRule[], field: string) => {
    const [start, end] = rules.find(rule => rule.field === field)?.timestampValues ?? [];
    if (!start || !end) return undefined;

    // FilterPanel stores date-only inputs as UTC midnight; rebuild them as local dates
    // before expanding the inclusive request range.
    const localDate = (value: Date) => new Date(
        value.getUTCFullYear(),
        value.getUTCMonth(),
        value.getUTCDate()
    );

    return {start: startOfDay(localDate(start)), end: endOfDay(localDate(end))};
};

const GuestAppUsersList = () => {
    useTrackScreenVisit('system guest app users');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const filterOptions = useMemo<FilterOption[]>(() => [
        {
            field: 'email',
            name: t('Email'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.STARTS_WITH],
            predefinedValues: [],
            dataType: DataType.TEXT
        },
        {
            field: 'phone',
            name: t('Phone'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.CONTAINS],
            predefinedValues: [],
            dataType: DataType.TEXT
        },
        {
            field: 'language',
            name: t('Language'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.EQUAL],
            predefinedValues: [],
            dataType: DataType.TEXT
        },
        {
            field: 'lastActivity',
            name: t('Last Activity'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.BETWEEN],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        },
        {
            field: 'createdAt',
            name: t('Created'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.BETWEEN],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        },
        {
            field: 'updatedAt',
            name: t('Last updated'),
            localizations: {},
            description: '',
            allowedOperators: [DataTypeOperator.BETWEEN],
            predefinedValues: [],
            dataType: DataType.TIMESTAMP
        }
    ], [t]);

    const columns = useMemo<ColumnDef<GuestAppUser>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: guestAppUser => formatProfileListName(guestAppUser.profile),
            cell: ({row}) => <Box>{formatProfileListName(row.original.profile) || '-'}</Box>,
            size: 220,
            meta: {defaultActive: true, grow: true, printable: true, printDefault: true}
        },
        {
            id: 'email',
            header: t('Email'),
            accessorFn: guestAppUser => guestAppUser.email,
            cell: ({row}) => <Box>{row.original.email || '-'}</Box>,
            size: 260,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'phone',
            header: t('Phone'),
            accessorFn: guestAppUser => guestAppUser.phone,
            cell: ({row}) => <Box>{row.original.phone || '-'}</Box>,
            size: 180,
            meta: {defaultActive: true, printable: true, printDefault: true}
        },
        {
            id: 'language',
            header: t('Language'),
            accessorFn: guestAppUser => guestAppUser.language,
            cell: ({row}) => <Box>{row.original.language || '-'}</Box>,
            size: 140,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <LanguagesElement
                        languageCodes={row.original.language ? [row.original.language] : undefined}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'lastActivity',
            header: t('Last Activity'),
            accessorFn: guestAppUser => guestAppUser.lastActivity,
            cell: ({row}) => <DateElement date={row.original.lastActivity} dateOnly/>,
            size: 180,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.lastActivity} dateOnly/>
            }
        },
        {
            id: 'insertDate',
            header: t('Created'),
            accessorFn: guestAppUser => guestAppUser.insertDate,
            cell: ({row}) => <DateElement date={row.original.insertDate}/>,
            size: 200,
            meta: {
                defaultActive: false,
                isServerSortable: true,
                printable: true,
                printCell: row => <DateElement date={row.original.insertDate}/>
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: guestAppUser => guestAppUser.id,
            cell: ({row}) => <Box>{row.original.id || ''}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: guestAppUser => guestAppUser.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.updateDate}/>
            }
        }
    ], [t]);
    const controller = useServerTable({
        tableId: 'system-guest-app-users',
        columns,
        getRowId: guestAppUser => guestAppUser.id!,
        onRowClick: guestAppUser => navigate(`/system/guestAppUsers/${guestAppUser.id}`),
        initialSorting: [{id: 'updateDate', desc: true}],
        queryFn: async ({pagination, searchQuery, filterRules, sorting}) => {
            const serverSort = sorting
                ? [
                    sorting,
                    ...(sorting.field === 'updateDate'
                        ? [{field: '_id', direction: sorting.direction}]
                        : [])
                ]
                : undefined;
            const request: GuestAppListRequestDTO = {
                query: searchQuery || undefined,
                email: textFilterValues(filterRules, 'email'),
                phone: textFilterValues(filterRules, 'phone'),
                language: textFilterValues(filterRules, 'language'),
                lastActivity: dateRangeFilter(filterRules, 'lastActivity'),
                createdAt: dateRangeFilter(filterRules, 'createdAt'),
                updatedAt: dateRangeFilter(filterRules, 'updatedAt'),
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: serverSort
                }
            };
            const page = await GuestAppUserService.getAllGuestAppUsers(request);

            return {
                content: page.content ?? [],
                totalElements: page.totalElements ?? 0
            };
        }
    });

    return (
        <SettingsListPage title={t('App users')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFilter options={filterOptions}/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                        <ServerTablePrint title={t('App users')}/>

                        <ServerTableCreate
                            label={t('Create guest app user')}
                            onClick={() => navigate('/system/guestAppUsers/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default GuestAppUsersList;
